import { Button, Dialog } from '@today/ui-primitives'
import type { ReactElement } from 'react'

/** Copy of {@link ApiKeyRevokeDialogView}; each omitted key falls back to English. */
export interface ApiKeyRevokeDialogLabels {
	title: string
	/** Shown after the key's name. */
	body: string
	confirm: string
	cancel: string
	close: string
}

const DEFAULT_LABELS: ApiKeyRevokeDialogLabels = {
	title: 'Revoke API key',
	body: 'Integrations using this key stop working immediately. A revoked key cannot be restored.',
	confirm: 'Revoke',
	cancel: 'Cancel',
	close: 'Close',
}

/**
 * Props for {@link ApiKeyRevokeDialogView}.
 *
 * @property keyName - The key being revoked; the dialog is open exactly while it is non-null.
 * @property error - A server refusal shown inside the still-open dialog.
 */
export interface ApiKeyRevokeDialogViewProps {
	keyName: string | null
	error?: string | null
	isBusy?: boolean
	onConfirm: () => void
	onCancel: () => void
	labels?: Partial<ApiKeyRevokeDialogLabels>
}

/** Confirms revoking a key — terminal, so it is always asked (organism, TO-516). */
export function ApiKeyRevokeDialogView(props: ApiKeyRevokeDialogViewProps): ReactElement {
	const text = { ...DEFAULT_LABELS, ...props.labels }
	return (
		<Dialog
			isOpen={props.keyName !== null}
			onOpenChange={(open) => (open ? undefined : props.onCancel())}
			title={text.title}
			labels={{ close: text.close }}
		>
			<div className="flex flex-col gap-3">
				{props.error ? (
					<p role="alert" className="text-ds-sm text-ds-danger">
						{props.error}
					</p>
				) : null}
				<p className="text-ds-sm font-medium text-ds-text">{props.keyName}</p>
				<p className="text-ds-sm text-ds-text">{text.body}</p>
				<div className="mt-1 flex gap-2">
					<Button variant="danger" isDisabled={props.isBusy} onPress={props.onConfirm}>
						{text.confirm}
					</Button>
					<Button variant="muted" onPress={props.onCancel}>
						{text.cancel}
					</Button>
				</div>
			</div>
		</Dialog>
	)
}
